import { useState } from "react";
import { Cross2Icon } from "@radix-ui/react-icons";
import clsx from "clsx";
import { Button } from "react-migration/components/DeprecatedButton";
import { DeprecatedModal as Modal } from "react-migration/components/DeprecatedModal";
import { Alert } from "react-migration/components/Alert";
import type { SiteFeature } from "src/js/stores/siteGeometry/store";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { logEvent } from "react-migration/lib/util/logEvent";
import { useSnapshot } from "valtio";
import { SiteButton } from "react-migration/layouts/map/Multilayer/layer_types/SitesLayerType/SiteButton";
import { mapStore } from "src/js/stores/map/store";

interface MergeSiteModalProps {
  handleCancelSave(): void;
  handleSaveAsSiteAs(): void;
  handleMergeSave(selectedMergeSiteId: string | undefined): void;
  intersectingSites: SiteFeature[];
  hasError: boolean;
  setHasError: (v: boolean) => void;
  disabled?: boolean;
}

export const MergeSiteModal = ({
  handleCancelSave,
  handleSaveAsSiteAs,
  handleMergeSave,
  intersectingSites,
  disabled,
  hasError,
  setHasError,
}: MergeSiteModalProps) => {
  const { t } = useTranslation();
  const { googleMap } = useSnapshot(mapStore);
  const [selectedMergeSiteId, setSelectedMergeSiteId] = useState<string>();

  function openHelp() {
    const center = googleMap?.getCenter();
    if (center) {
      logEvent("Open Intercom Widget", { from: "failed to save site" });
      window?.Intercom(
        "showNewMessage",
        t("sites.save_site.merge_site_modal.contact_support_message", {
          coordinates: JSON.stringify(center.toJSON()),
        })
      );
    }
  }

  return (
    <Modal
      title={t("sites.save_site.merge_site_modal.title")}
      titleClasses="p-4"
      contentClasses="p-0! text-sm max-w-lg"
    >
      <button
        onClick={handleCancelSave}
        className="hover:bg-neutral-300 absolute top-[10px] right-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:outline-hidden"
      >
        <Cross2Icon />
      </button>
      <div className="px-6 py-4">
        <p className="font-bold">{t("sites.save_site.merge_site_modal.prompt_text")}</p>
        <p>{t("sites.save_site.merge_site_modal.prompt_text_secondary")}</p>

        <div className="mt-4 flex flex-col items-baseline gap-y-2 max-h-[40vh] overflow-auto">
          {intersectingSites.map((site) => {
            const isSelected = selectedMergeSiteId === site.id;

            return (
              <label className="flex! flex-row w-full overflow-hidden shrink-0" key={`match-${site.id}`}>
                <input
                  className="mr-2"
                  type="radio"
                  value={site.id}
                  checked={isSelected}
                  onChange={(e) => setSelectedMergeSiteId(e.target.value)}
                />
                <div
                  className={clsx("grow border rounded-sm overflow-hidden", {
                    "border-neutral-200": !isSelected,
                    "border-border-action-focus bg-platform-50": isSelected,
                  })}
                >
                  <SiteButton
                    site={site}
                    onClick={() => setSelectedMergeSiteId(isSelected ? undefined : String(site.id))}
                  />
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {hasError && (
        <div className="px-4">
          <Alert.Warning onClickClose={() => setHasError(false)}>
            {t("sites.save_site.merge_site_modal.failed", {
              contactSupport: (
                <span className="cursor-pointer text-platform-600" onClick={openHelp}>
                  {t("sites.save_site.merge_site_modal.contact_support")}
                </span>
              ),
            })}
          </Alert.Warning>
        </div>
      )}

      <div className="flex justify-end gap-2 px-4 py-4">
        <Button.Secondary disabled={disabled} onClick={handleCancelSave}>
          {t("sites.save_site.merge_site_modal.cancel")}
        </Button.Secondary>
        <Button.Primary disabled={disabled} onClick={handleSaveAsSiteAs}>
          {t("sites.save_site.merge_site_modal.save_as_site")}
        </Button.Primary>
        <Button.Primary
          onClick={() => handleMergeSave(selectedMergeSiteId)}
          disabled={disabled || !selectedMergeSiteId}
        >
          {t("sites.save_site.merge_site_modal.merge_sites")}
        </Button.Primary>
      </div>
    </Modal>
  );
};
